import React, { useState, useEffect } from 'react';
import { FiCheck } from 'react-icons/fi';
import { getAuthContext } from '../../utils/jwt';
import { ToastContainer, toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

const FiCheckComponent = FiCheck as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface Plan {
  code: string;
  name: string;
  priceLabel: string;
  highlights: string[];
}

interface AvailablePlansProps {
  currentPlan?: string;
}

const AvailablePlans: React.FC<AvailablePlansProps> = ({ currentPlan = 'FREE' }) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);

  const generateIdempotencyKey = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const idempotencyKey = generateIdempotencyKey();

      const response = await fetch(`${BASE_URL}/api/settings/billing/plans`, {
        method: 'GET',
        headers: {
          'X-Idempotency-Key': idempotencyKey,
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch plans: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' && Array.isArray(data.data)) {
        setPlans(data.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load plans');
      // console.error('Fetch plans error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const isCurrentPlan = (planCode: string): boolean => {
    return planCode === currentPlan;
  };

  const getButtonText = (planCode: string): string => {
    if (isCurrentPlan(planCode)) {
      return 'Your Plan';
    }
    return 'Buy Now';
  };

  const getButtonStyle = (planCode: string): string => {
    if (isCurrentPlan(planCode)) {
      return 'text-[#00A4BD] text-[12px] cursor-not-allowed border border-[#00A4BD]';
    }
    if (planCode === 'PREMIUM') {
      return 'bg-orange-500 text-white hover:bg-orange-600';
    }
    if (planCode === 'ENTERPRISE') {
      return 'bg-purple-500 text-white hover:bg-purple-600';
    }
    return 'bg-[#00A4BD] text-white hover:bg-[#007080]';
  };

  const getPlanBackground = (planCode: string): string => {
    switch (planCode) {
      case 'FREE':
        return 'bg-[#E0F8FB]';
      case 'PREMIUM':
        return 'bg-[#FFF1EC]';
      case 'ENTERPRISE':
        return 'bg-purple-50';
      default:
        return 'bg-[#E0F8FB]';
    }
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center py-12'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#00899E]'></div>
      </div>
    );
  }

  return (
    <>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        {plans.map((plan) => {
          const isPopular = plan.code === 'PREMIUM';

          return (
            <div
              key={plan.code}
              className={`border rounded-lg p-4 shadow-md bg-white relative h-[max-content] ${isPopular ? 'hover:border-orange-400' : 'hover:border-[#00A4BD]'
                }`}
            >
              {/* Popular Badge */}
              {isPopular && (
                <span className='absolute top-[6px] left-[53px] -translate-x-1/2 bg-[#E6FAF3] text-[#07A26F] px-3 py-1 text-xs font-semibold rounded-full'>
                  Popular Plan
                </span>
              )}

              {/* Current Plan Badge */}
              {isCurrentPlan(plan.code) && (
                <span className='absolute top-[6px] right-4 bg-[#00899E] text-white px-3 py-1 text-xs font-semibold rounded-full'>
                  Current Plan
                </span>
              )}

              <div className={`${getPlanBackground(plan.code)} p-8 text-center rounded-lg`}>
                {/* Plan Header */}
                <h3 className='text-[12px] font-semibold text-[#1F2937] uppercase mb-3'>
                  {plan.name}
                </h3>
                <p className='text-[32px] font-bold text-[#3F5265]'>
                  {plan.priceLabel}
                </p>
                <div className='flex justify-center'>
                  <p className='text-[#3F5265] text-[16px] mb-6 w-2/3'>
                    {plan.code === 'FREE' && 'For beginners exploring the platform'}
                    {plan.code === 'PREMIUM' && 'For growing teams ready to scale'}
                    {plan.code === 'ENTERPRISE' && 'For businesses serious about engagement'}
                  </p>
                </div>

                {/* Button */}
                <button
                  className={`w-1/3 py-2 px-2 rounded font-semibold text-sm ${getButtonStyle(plan.code)}`}
                  disabled={isCurrentPlan(plan.code)}
                >
                  {getButtonText(plan.code)}
                </button>
              </div>

              {/* Highlights from API */}
              <ul className='mt-6 space-y-2 text-[12px] text-[#657584]'>
                {plan.highlights && plan.highlights.map((highlight, idx) => (
                  <li key={idx} className='flex items-start gap-2'>
                    <FiCheckComponent className='text-[#07A26F] font-bold mt-[2px]' />
                    {highlight}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <ToastContainer position='top-right' autoClose={3000} />
    </>
  );
};

export default AvailablePlans;